"use strict";
// Shift hours — D14 and the settled decision "display field on the job, with a
// timezone conversion helper. Never used in calculations." Entered in BD time; shown
// converted to the one client zone the job picked (Q2, product owner 2026-09-28).
//
// Pure `Intl`, no date library: the web app ships none, and one conversion does not
// justify one. The offset is read for a specific DATE, because the answer changes
// with daylight saving — 18:00 BD is 08:00 in New York in summer and 07:00 in winter.
Object.defineProperty(exports, "__esModule", { value: true });
exports.zoneOffsetMinutes = zoneOffsetMinutes;
exports.convertShift = convertShift;
const MINUTES_PER_DAY = 24 * 60;
/** The zone's offset from UTC, in minutes, at one instant. */
function zoneOffsetMinutes(zone, at) {
    const parts = new Intl.DateTimeFormat('en-US', {
        timeZone: zone,
        hourCycle: 'h23',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
    }).formatToParts(at);
    const read = (type) => Number(parts.find((part) => part.type === type)?.value ?? 0);
    const asUtc = Date.UTC(read('year'), read('month') - 1, read('day'), read('hour'), read('minute'));
    const atMinute = Math.floor(at.getTime() / 60000) * 60000;
    return Math.round((asUtc - atMinute) / 60000);
}
function zoneAbbreviation(zone, at) {
    const part = new Intl.DateTimeFormat('en-US', { timeZone: zone, timeZoneName: 'short' })
        .formatToParts(at)
        .find((candidate) => candidate.type === 'timeZoneName');
    return part?.value ?? zone;
}
function dayShift(days) {
    if (days < 0)
        return -1;
    if (days > 0)
        return 1;
    return 0;
}
function toMinutes(time) {
    const [hours, minutes] = time.split(':').map(Number);
    return (hours ?? 0) * 60 + (minutes ?? 0);
}
function toTime(minutes) {
    const wrapped = ((minutes % MINUTES_PER_DAY) + MINUTES_PER_DAY) % MINUTES_PER_DAY;
    const hours = Math.floor(wrapped / 60);
    return `${String(hours).padStart(2, '0')}:${String(wrapped % 60).padStart(2, '0')}`;
}
/**
 * Converts a BD-time shift into `toZone` as it falls on `onDate` (`YYYY-MM-DD`, a BD
 * calendar day). The shift STARTS on that day; an end earlier than the start is the
 * next morning, which is what "6pm–3am" means.
 */
function convertShift(shift, fromZone, toZone, onDate) {
    const [year, month, day] = onDate.split('-').map(Number);
    const startMinutes = toMinutes(shift.start);
    const endMinutes = toMinutes(shift.end);
    const lengthMinutes = (endMinutes - startMinutes + MINUTES_PER_DAY) % MINUTES_PER_DAY;
    // Wall-clock start in the source zone → a real instant. Using the offset at a first
    // guess and then re-reading it at the result handles a start inside a DST change.
    const naiveUtc = Date.UTC(year ?? 1970, (month ?? 1) - 1, day ?? 1) + startMinutes * 60000;
    const guess = naiveUtc - zoneOffsetMinutes(fromZone, new Date(naiveUtc)) * 60000;
    const startInstant = naiveUtc - zoneOffsetMinutes(fromZone, new Date(guess)) * 60000;
    const endInstant = startInstant + lengthMinutes * 60000;
    const startOffset = zoneOffsetMinutes(toZone, new Date(startInstant));
    const endOffset = zoneOffsetMinutes(toZone, new Date(endInstant));
    const localStart = startInstant / 60000 + startOffset;
    const localEnd = endInstant / 60000 + endOffset;
    const startDay = Math.floor(localStart / MINUTES_PER_DAY);
    const sourceDay = Math.floor(naiveUtc / 60000 / MINUTES_PER_DAY);
    return {
        start: toTime(localStart),
        end: toTime(localEnd),
        zoneAbbreviation: zoneAbbreviation(toZone, new Date(startInstant)),
        startDayShift: dayShift(startDay - sourceDay),
        isOvernight: Math.floor(localEnd / MINUTES_PER_DAY) !== startDay,
    };
}
//# sourceMappingURL=shift.js.map